import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PersonalEntryDialog, fileUrl } from "@/components/PersonalEntryDialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Paperclip, LockSimple } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function PersonalEntries({ year }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState({ expense: [], income: [] });
  const [editing, setEditing] = useState(null);
  const loadCats = () => api.get("/personal/categories").then((r) => setCats(r.data));
  const load = () => api.get("/personal/entries", { params: { year } }).then((r) => setItems(r.data));
  useEffect(() => { loadCats(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year]);

  const label = (e) => [...cats.expense, ...cats.income].find((c) => c.key === e.category)?.label || e.category;
  const remove = async (id) => { if (!window.confirm("Excluir este lançamento?")) return; await api.delete(`/personal/entries/${id}`); toast.success("Excluído"); load(); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <div>
          <CardTitle className="text-lg">Lançamentos pessoais de {year}</CardTitle>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><LockSimple size={12} weight="fill" /> Só você vê. A empresa não tem acesso.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setEditing({ kind: "expense" })} data-testid="btn-new-personal-expense" className="bg-rose-600 hover:bg-rose-700 text-white gap-1"><Plus size={16} /> Despesa</Button>
          <Button variant="outline" onClick={() => setEditing({ kind: "income" })} data-testid="btn-new-personal-income" className="gap-1"><Plus size={16} /> Renda</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-500">Nenhum lançamento neste ano.</p>}
        {items.map((e) => (
          <div key={e.id} data-testid={`personal-row-${e.id}`} className="flex items-center gap-3 border rounded-lg p-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{label(e)}{e.description ? ` — ${e.description}` : ""}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2">{fmtDate(e.date)}
                {e.receipt_file_id && <a href={fileUrl(e.receipt_file_id)} target="_blank" rel="noreferrer" className="text-blue-700 inline-flex items-center gap-0.5"><Paperclip size={12} /> comprovante</a>}
              </div>
            </div>
            <div className={`font-bold whitespace-nowrap ${e.kind === "income" ? "text-emerald-700" : "text-rose-700"}`}>{e.kind === "income" ? "+" : "−"} {fmt(e.value)}</div>
            <Button size="icon" variant="ghost" onClick={() => setEditing(e)} data-testid={`btn-edit-personal-${e.id}`}><PencilSimple size={16} className="text-blue-600" /></Button>
            <Button size="icon" variant="ghost" onClick={() => remove(e.id)} data-testid={`btn-delete-personal-${e.id}`}><Trash size={16} className="text-red-600" /></Button>
          </div>
        ))}
      </CardContent>
      <PersonalEntryDialog entry={editing} cats={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onCatsChanged={loadCats} />
    </Card>
  );
}
